import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Bar, BarChart, CartesianGrid, Legend, Tooltip, XAxis, YAxis } from "recharts";
import type { ColumnDef } from "@tanstack/react-table";

import { ChartFrame, DataTable, FilterBar } from "@/components/data";
import type { DataTablePage, FilterSpec } from "@/components/data";
import type { FilterValues } from "@/lib/filters";
import { FreshnessLine } from "@/components/shell/FreshnessLine";
import { AlertCard } from "@/modules/protex/components/AlertCard";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import {
  getFindings,
  getFindingsForWeek,
  getWeeks,
  findingsKey,
  findingsWeekKey,
  messageFor,
  weeksKey,
  type FindingRow,
  type FindingsBlockingFilter,
  type FindingsStatusFilter,
  type Grain,
} from "./api";
import { Chip } from "./components/Chip";
import { GrainToggle } from "./components/GrainToggle";
import { currentMonth, formatMonthLabel, recentMonths } from "./components/monthLabel";
import { readGrain, readPeriod } from "./components/period";
import { formatWeekRange } from "./components/weekLabel";

const ORIGIN_LABEL: Record<string, string> = {
  unknown: "Not set",
  "kb-synthesis": "KB synthesis",
  takeaway: "Takeaway",
};

function originLabel(stage: string): string {
  return ORIGIN_LABEL[stage] ?? stage;
}

const TREND_COLOR: Record<string, string> = {
  unknown: "var(--color-ks-grey-2)",
  "kb-synthesis": "var(--color-ks-purple)",
  takeaway: "var(--color-ks-warn)",
};

export function AccuracyFindingsTab({ areaName }: { areaName: string }) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();

  // "Month is its current behaviour" (cvce-pipeline-4 spec) — absent an
  // explicit `grain`, this tab keeps defaulting to month, unlike the
  // Pipeline tab's "week" default.
  const grain = readGrain(search, "month");
  const defaultMonth = useMemo(() => currentMonth(), []);
  const weeksQuery = useQuery({ queryKey: weeksKey, queryFn: getWeeks, enabled: grain === "week" });
  const defaultPeriod = grain === "week" ? weeksQuery.data?.data.default_week ?? null : defaultMonth;
  const period = readPeriod(search, grain) ?? defaultPeriod;
  const status = (typeof search.status === "string" ? search.status : "all") as FindingsStatusFilter;
  const blocking = (typeof search.blocking === "string" ? search.blocking : "all") as FindingsBlockingFilter;
  const [recheckOnly, setRecheckOnly] = useState(false);
  const [cellFilter, setCellFilter] = useState<{ problemType: string; originStage: string } | null>(null);

  const monthQuery = useQuery({
    queryKey: findingsKey(period ?? defaultMonth, status, blocking),
    queryFn: () => getFindings(period ?? defaultMonth, status, blocking),
    enabled: grain === "month" && Boolean(period),
  });
  const weekQuery = useQuery({
    queryKey: findingsWeekKey(period ?? "pending", status, blocking),
    queryFn: () => getFindingsForWeek(period as string, status, blocking),
    enabled: grain === "week" && Boolean(period),
  });
  const query = grain === "week" ? weekQuery : monthQuery;

  const periodSpec: FilterSpec =
    grain === "week"
      ? {
          name: "period",
          label: "Week",
          kind: "choice",
          options: (weeksQuery.data?.data.weeks ?? []).map((w) => ({
            value: w.week_start,
            label: formatWeekRange(w.week_start),
          })),
        }
      : {
          name: "period",
          label: "Month",
          kind: "choice",
          options: recentMonths(defaultMonth).map((m) => ({ value: m, label: formatMonthLabel(m) })),
        };

  const setGrain = (nextGrain: Grain) => {
    setCellFilter(null);
    const next: Record<string, unknown> = { ...search, grain: nextGrain };
    delete next.month;
    delete next.period;
    void navigate({ to: ".", search: next, replace: true });
  };

  const setPeriod = (next: FilterValues) => {
    setCellFilter(null);
    const nextSearch: Record<string, unknown> = { ...search, grain, period: next.period };
    delete nextSearch.month;
    void navigate({ to: ".", search: nextSearch, replace: true });
  };

  const setStatus = (next: FindingsStatusFilter) => {
    void navigate({
      to: ".",
      search: { ...search, grain, period, status: next === "all" ? undefined : next },
      replace: true,
    });
  };
  const setBlocking = (next: FindingsBlockingFilter) => {
    void navigate({
      to: ".",
      search: { ...search, grain, period, blocking: next === "all" ? undefined : next },
      replace: true,
    });
  };

  const openAsset = (assetId: string) => {
    void navigate({ to: ".", search: { ...search, grain, period, asset: assetId }, replace: false });
  };

  if (query.isLoading || (grain === "week" && weeksQuery.isLoading)) {
    return (
      <div className="flex flex-col gap-3" aria-busy="true">
        <p role="status" className="sr-only">
          Loading accuracy findings…
        </p>
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-24 animate-pulse rounded-ks-md bg-ks-grey-4/40" />
        ))}
      </div>
    );
  }

  if (query.isError || query.data?.data.available === false) {
    return (
      <AlertCard
        title="We couldn't load accuracy findings"
        detail={query.error ? messageFor(query.error, areaName) : "Try again shortly."}
      />
    );
  }

  const data = query.data!.data;

  let rows = data.rows;
  if (recheckOnly) rows = rows.filter((r) => r.newer_draft_since_review);
  if (cellFilter) rows = rows.filter((r) => r.problem_type === cellFilter.problemType && r.origin_stage === cellFilter.originStage);

  const problemTypes = [...new Set(data.matrix.map((c) => c.problem_type))].sort();
  const originStages = [...new Set(data.matrix.map((c) => c.origin_stage))].sort();
  const cellN = (problemType: string, originStage: string) =>
    data.matrix.find((c) => c.problem_type === problemType && c.origin_stage === originStage)?.n ?? 0;

  const page: DataTablePage = { pageIndex: 0, pageSize: rows.length || 1, rowCount: rows.length };
  const columns: ColumnDef<FindingRow, unknown>[] = [
    { accessorKey: "asset_id", header: "Asset" },
    {
      accessorKey: "claim",
      header: "Sentence",
      cell: ({ row }) => {
        const text = row.original.claim;
        return text.length > 80 ? `${text.slice(0, 80)}…` : text;
      },
    },
    { accessorKey: "problem_type", header: "Type" },
    { id: "origin", header: "Origin", cell: ({ row }) => originLabel(row.original.origin_stage) },
    {
      id: "blocking",
      header: "Blocking",
      cell: ({ row }) => (row.original.blocking ? "Yes" : "No"),
    },
    { accessorKey: "status", header: "Status" },
    {
      id: "recheck",
      header: "",
      cell: ({ row }) =>
        row.original.newer_draft_since_review ? (
          <span className="rounded-ks-sm border border-ks-purple-3 bg-ks-purple-4 px-2 py-0.5 text-[11px] font-semibold text-ks-purple">
            Newer draft since review
          </span>
        ) : null,
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2.5">
        <GrainToggle grain={grain} onChange={setGrain} />
        <FilterBar
          layout="inline"
          filters={[periodSpec]}
          values={{ period: period ?? "" }}
          defaultValues={{ period: defaultPeriod ?? "" }}
          onChange={setPeriod}
        />
      </div>
      <FreshnessLine meta={query.data!.meta} pending={query.isFetching} />

      <div className="flex flex-wrap items-center gap-2">
        <Chip label="Open" active={status === "open"} onClick={() => setStatus(status === "open" ? "all" : "open")} />
        <Chip
          label="Blocking"
          active={blocking === "true"}
          onClick={() => setBlocking(blocking === "true" ? "all" : "true")}
        />
        <Chip label="Waiting for re-check" active={recheckOnly} onClick={() => setRecheckOnly((v) => !v)} />
      </div>

      {data.reviewed_assets === 0 ? (
        <p className="text-sm text-ks-ink">No reviews in this period. This is not the same as zero errors.</p>
      ) : (
        <>
          {data.biggest_open_group === null ? null : (
            <div className="flex items-center justify-between gap-3 rounded-ks-md border border-ks-grey-4 bg-ks-surface p-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-ks-grey-1">Biggest open group</p>
                <p className="text-sm text-ks-ink">
                  {data.biggest_open_group.origin_stage === "unknown" ? (
                    <>
                      {data.biggest_open_group.n} {data.biggest_open_group.problem_type} findings.{" "}
                      <span className="text-ks-grey-1">Origin not set. Triage in B6.</span>
                    </>
                  ) : (
                    <>
                      {data.biggest_open_group.n} {data.biggest_open_group.problem_type} findings from{" "}
                      {originLabel(data.biggest_open_group.origin_stage)}.
                    </>
                  )}
                </p>
              </div>
              <button
                type="button"
                data-nonmutating
                onClick={() =>
                  setCellFilter({
                    problemType: data.biggest_open_group!.problem_type,
                    originStage: data.biggest_open_group!.origin_stage,
                  })
                }
                className="h-8 shrink-0 rounded-ks-sm border border-ks-purple-3 bg-ks-surface px-3 text-[13px] text-ks-purple"
              >
                Open
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            <KpiCard label="Findings per asset" value={data.kpis.findings_per_asset?.toFixed(1) ?? null} />
            <KpiCard
              label="Findings per 1,000 words"
              value={data.kpis.findings_per_1000_words != null ? data.kpis.findings_per_1000_words.toFixed(1) : null}
              note={data.kpis.findings_per_1000_words == null ? "Not measured yet" : undefined}
            />
            <KpiCard
              label="Clean reviews"
              value={data.kpis.clean_review_rate != null ? `${(data.kpis.clean_review_rate * 100).toFixed(1)}%` : null}
            />
            <KpiCard
              label="Median time to resolve"
              value={data.kpis.median_resolution_days != null ? `${data.kpis.median_resolution_days.toFixed(1)} days` : null}
              note={data.kpis.median_resolution_days == null ? "None resolved yet" : undefined}
            />
            <KpiCard
              label="Unattributed"
              value={data.kpis.unattributed_share != null ? `${(data.kpis.unattributed_share * 100).toFixed(1)}%` : null}
            />
          </div>

          {problemTypes.length > 0 && (
            <div className="rounded-ks-md border border-ks-grey-4 bg-ks-surface p-3">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Problem type</TableHead>
                    {originStages.map((o) => (
                      <TableHead key={o}>{originLabel(o)}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {problemTypes.map((p) => (
                    <TableRow key={p}>
                      <TableCell className="text-xs font-semibold text-ks-ink">{p}</TableCell>
                      {originStages.map((o) => {
                        const n = cellN(p, o);
                        return (
                          <TableCell key={o}>
                            {n > 0 ? (
                              <button
                                type="button"
                                data-nonmutating
                                data-matrix-cell={`${p}:${o}`}
                                onClick={() => setCellFilter({ problemType: p, originStage: o })}
                                className="h-7 w-10 rounded-ks-sm border border-ks-grey-4 bg-ks-grey-4/40 text-xs font-semibold text-ks-ink hover:bg-ks-purple-4"
                              >
                                {n}
                              </button>
                            ) : (
                              <span className="text-xs text-ks-grey-3">–</span>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          <ChartFrame
            title={grain === "week" ? "Findings by week and origin" : "Findings by month and origin"}
            titleVisible
            pointCount={data.trend.length}
          >
            <BarChart data={trendRows(data.trend)} margin={{ top: 12, right: 16, bottom: 4, left: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-ks-grey-4)" vertical={false} />
              <XAxis dataKey="period" stroke="var(--color-ks-grey-1)" />
              <YAxis stroke="var(--color-ks-grey-1)" />
              <Tooltip />
              <Legend />
              {originStages.map((o) => (
                <Bar key={o} dataKey={o} stackId="findings" name={originLabel(o)} fill={TREND_COLOR[o] ?? "var(--color-ks-grey-2)"} isAnimationActive={false} />
              ))}
            </BarChart>
          </ChartFrame>

          {cellFilter && (
            <button
              type="button"
              data-nonmutating
              onClick={() => setCellFilter(null)}
              className="w-fit rounded-ks-sm border border-ks-purple-3 bg-ks-surface px-3 py-1 text-[13px] text-ks-purple"
            >
              Clear cell filter ({cellFilter.problemType} · {originLabel(cellFilter.originStage)})
            </button>
          )}

          <DataTable
            columns={columns}
            rows={rows}
            page={page}
            onPageChange={() => {}}
            onRowClick={(row) => openAsset(row.asset_id)}
          />
        </>
      )}
    </div>
  );
}

function trendRows(trend: { month?: string; week_start?: string; origin_stage: string; n: number }[]) {
  const byPeriod = new Map<string, Record<string, string | number>>();
  for (const t of trend) {
    const key = t.month ?? t.week_start ?? "";
    const row = byPeriod.get(key) ?? { period: key };
    row[t.origin_stage] = t.n;
    byPeriod.set(key, row);
  }
  return [...byPeriod.values()];
}

function KpiCard({ label, value, note }: { label: string; value: string | null; note?: string }) {
  return (
    <div className="flex flex-col gap-1 rounded-ks-md border border-ks-grey-4 bg-ks-surface p-3">
      <span className="text-xs font-semibold uppercase tracking-wide text-ks-grey-1">{label}</span>
      <span className="text-2xl font-bold text-ks-ink">{value ?? "–"}</span>
      <span className="text-[11px] text-ks-grey-1">{note ?? "No target yet"}</span>
    </div>
  );
}
